import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { articlesAPI } from '../lib/api';
import { Layout } from '../components/layout/Layout';
import { ArticleCard } from '../components/articles/ArticleCard';
import { formatDate, getImageUrl } from '../lib/utils';
import { Skeleton } from '../components/ui/skeleton';
import { Facebook, Twitter, Linkedin, Link2, ArrowLeft } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

// Helper function to convert YouTube URLs to proper embed format
const processArticleBody = (html) => {
  if (!html) return html;
  
  // Convert youtu.be URLs to embed format
  // Matches: youtu.be/VIDEO_ID or youtu.be/VIDEO_ID?si=xxx
  let processed = html.replace(
    /src=["'](https?:\/\/youtu\.be\/([a-zA-Z0-9_-]+)(?:\?[^"']*)?)["']/gi,
    'src="https://www.youtube.com/embed/$2"'
  );
  
  // Convert youtube.com/watch?v= URLs to embed format
  processed = processed.replace(
    /src=["'](https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)(?:&[^"']*)?)["']/gi,
    'src="https://www.youtube.com/embed/$2"'
  );
  
  // Convert youtube.com/shorts/ URLs to embed format
  processed = processed.replace(
    /src=["'](https?:\/\/(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]+)(?:\?[^"']*)?)["']/gi,
    'src="https://www.youtube.com/embed/$2"'
  );
  
  // Add responsive wrapper to iframes
  processed = processed.replace(
    /<iframe([^>]*)><\/iframe>/gi,
    '<div class="video-embed-wrapper" style="position:relative;padding-bottom:56.25%;height:0;overflow:hidden;margin:2rem 0;"><iframe$1 style="position:absolute;top:0;left:0;width:100%;height:100%;"></iframe></div>'
  );
  
  return processed;
};

export const ArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const res = await articlesAPI.getBySlug(slug);
        setArticle(res.data);

        // Record view
        await articlesAPI.recordView(res.data.id);

        // Fetch related articles from same category
        if (res.data.category_id) {
          const relatedRes = await articlesAPI.getPublished({ 
            category_slug: res.data.category?.slug,
            limit: 4 
          });
          setRelatedArticles(
            relatedRes.data.articles.filter(a => a.id !== res.data.id).slice(0, 3)
          );
        }
      } catch (error) {
        console.error('Error fetching article:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  const shareUrl = window.location.href;

  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success('Link copied to clipboard');
  };

  if (loading) {
    return (
      <Layout>
        <div className="container-custom py-12">
          <Skeleton className="h-8 w-32 mb-8" />
          <Skeleton className="h-12 w-3/4 mb-4" />
          <Skeleton className="h-6 w-1/2 mb-8" />
          <Skeleton className="aspect-video w-full mb-8" />
          <div className="max-w-2xl mx-auto space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!article) {
    return (
      <Layout>
        <div className="container-custom py-24 text-center">
          <h1 className="font-serif text-3xl font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-8">
            The article you're looking for doesn't exist or has been removed.
          </p>
          <Link to="/">
            <Button>Back to Home</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const heroImage = article.hero_image 
    ? getImageUrl(article.hero_image) 
    : 'https://images.unsplash.com/photo-1648260029310-5f1da359af9d?w=1920&h=1080&fit=crop';

  return (
    <Layout>
      <article data-testid="article-detail">
        {/* Back Link */}
        <div className="container-custom pt-6">
          <Link 
            to={article.category ? `/category/${article.category.slug}` : '/'}
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to {article.category?.name || 'Home'}
          </Link>
        </div>

        {/* Header */}
        <header className="container-custom py-8 md:py-12 max-w-4xl mx-auto text-center">
          {article.is_breaking && (
            <span className="breaking-badge mb-4 inline-block" data-testid="article-breaking-badge">
              Breaking
            </span>
          )}
          {article.category && (
            <Link 
              to={`/category/${article.category.slug}`}
              className="category-badge mb-4 block"
              data-testid="article-category"
            >
              {article.category.name}
            </Link>
          )}
          <h1 className="font-serif text-3xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" data-testid="article-title">
            {article.title}
          </h1>
          {article.subheadline && (
            <p className="text-xl md:text-2xl text-muted-foreground leading-relaxed mb-8" data-testid="article-subheadline">
              {article.subheadline}
            </p>
          )}
          <div className="article-meta flex items-center justify-center gap-4">
            <span className="font-medium text-foreground">{article.author}</span>
            <span>·</span>
            <span>{formatDate(article.publish_date || article.created_at)}</span>
          </div>
        </header>

        {/* Hero Media */}
        {article.hero_type === 'video' ? (
          // Check if it's an uploaded video file or a URL
          article.hero_video_type === 'upload' && article.hero_video_file ? (
            <div className="max-w-5xl mx-auto mb-12">
              <video
                src={getImageUrl(article.hero_video_file)}
                className="w-full aspect-video object-cover bg-black"
                controls
                data-testid="article-video-uploaded"
              />
              {article.photo_credits && (
                <p className="text-center text-sm text-muted-foreground mt-4">
                  {article.photo_credits}
                </p>
              )}
            </div>
          ) : article.hero_video ? (
            <div className="aspect-video max-w-5xl mx-auto mb-12">
              <iframe
                src={article.hero_video.replace('watch?v=', 'embed/')}
                title={article.title}
                className="w-full h-full"
                allowFullScreen
                data-testid="article-video"
              />
            </div>
          ) : (
            <figure className="mb-12">
              <img
                src={heroImage}
                alt={article.title}
                className="w-full h-auto object-contain"
                data-testid="article-hero-image"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {article.photo_credits && (
                <figcaption className="container-custom text-center text-sm text-muted-foreground mt-4">
                  {article.photo_credits}
                </figcaption>
              )}
            </figure>
          )
        ) : (
          <figure className="mb-12">
            <img
              src={heroImage}
              alt={article.title}
              className="w-full h-auto object-contain"
              data-testid="article-hero-image"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            {article.photo_credits && (
              <figcaption className="container-custom text-center text-sm text-muted-foreground mt-4">
                {article.photo_credits}
              </figcaption>
            )}
          </figure>
        )}

        {/* Body */}
        <div className="container-custom max-w-2xl mx-auto">
          <div 
            className="article-body"
            dangerouslySetInnerHTML={{ __html: processArticleBody(article.body) }}
            data-testid="article-body"
          />

          {/* Share Buttons */}
          <div className="flex items-center gap-4 py-8 mt-8 border-t border-border" data-testid="share-buttons">
            <span className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
              Share
            </span>
            <a
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 hover:bg-secondary rounded-sm transition-colors"
              data-testid="share-twitter"
            >
              <Twitter className="h-5 w-5" />
            </a>
            <a
              href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 hover:bg-secondary rounded-sm transition-colors"
              data-testid="share-facebook"
            >
              <Facebook className="h-5 w-5" />
            </a>
            <a
              href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="p-2 hover:bg-secondary rounded-sm transition-colors"
              data-testid="share-linkedin"
            >
              <Linkedin className="h-5 w-5" />
            </a>
            <button
              onClick={handleCopyLink}
              className="p-2 hover:bg-secondary rounded-sm transition-colors"
              data-testid="share-copy"
            >
              <Link2 className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="bg-secondary/30 py-12 mt-12" data-testid="related-articles">
            <div className="container-custom">
              <h2 className="font-serif text-2xl md:text-3xl font-bold mb-8">
                Related Stories
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {relatedArticles.map((relArticle) => (
                  <ArticleCard key={relArticle.id} article={relArticle} />
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
    </Layout>
  );
};

export default ArticlePage;
